<template>
  <div class="form-group position-relative">
    <label class="form-label"
           :class="formLabelClass"
           v-if="showlabel && label">
      <span>{{ label }}</span>
      <span v-if="required"> *</span>
    </label>
    <div class="position-relative"
         v-if="editMode">
      <input v-if="multiple"
             class="form-control bg-white overflow-ellipsis"
             :value="valueText"
             @focus="focus"
             readonly
      />
      <SpaceduckMaskInput v-else
                 :model-value="contentStringMasked"
                 @update:model-value="contentStringMasked = $event"
                 @keyup.native="manualInput"
                 @focus.native="focus"
                 :readonly="readonly"
                 :placeholder="placeholder"
                 class="form-control"
                 mask="##.##.####" />
      <CalendarSvg class="position-absolute calendar-icon non-clickable"
                   v-if="calendarIcon && !readonly"/>
      <TrashSvg class="position-absolute calendar-icon svg-gray pointer"
                v-if="clearable && contentStringMasked"
                @click.native="clear"/>
    </div>
    <div v-if="show"
         v-click-outside="clickedOutside"
         class="date-picker"
         :class="{ labeled: !!label }">
      <VDatePicker :locale="{
                      id: lang,
                      firstDayOfWeek: 2,
                      masks: { L: 'DD.MM.YYYY' }
                   }"
                   :readonly="readonly"
                   @update:model-value="handleInput"
                   @dayclick="dayClicked"
                   :min-date="minDate"
                   :max-date="maxDate"
                   v-model="content"
                   :attributes='attrs'/>
    </div>
    <div v-if="!editMode">{{ contentStringMasked || '-' }}</div>
    <ValidationError class="mt-1"
                     v-if="showErrors"
                     :validationId="validationId"/>
  </div>
</template>

<script>

import moment from 'moment'
import { DatePicker } from 'v-calendar'
import ValidationError from "@/components/form/ValidationError"
import CalendarSvg from '@/assets/svg-vue/calendar/calendar.svg'
import TrashSvg from '@/assets/svg-vue/general/trash_tr.svg'
import SpaceduckMaskInput from "@/components/parts/general/SpaceduckMaskInput.vue"

export default {
  name: "DateGroup",
  emits: ['input', 'dayClicked'],
  components: {
    SpaceduckMaskInput,
    ValidationError,
    VDatePicker: DatePicker,
    CalendarSvg,
    TrashSvg,
    // TheMask
  },
  data() {
    return {
      show: false,
      date: new Date(),
      content: new Date(moment(this.value)),
      timer: null,
      contentString: this.value,
      contentStringMasked: this.value ? moment(this.value).format('DD.MM.YYYY') : null,
    }
  },
  props: {
    label: {},
    placeholder: {
      type: String,
      default: '__.__.____',
    },
    value: {},
    showErrors: {
      type: Boolean,
      default: false
    },
    closeOnClick: {
      type: Boolean,
      default: true
    },
    clearable: {
      type: Boolean,
      default: false
    },
    attrs: {
      type: Array,
      default: () => [{}]
    },
    showlabel: {
      type: Boolean,
      default: true
    },
    calendarIcon: {
      type: Boolean,
      default: true
    },
    readonly: {
      type: Boolean,
      default: false
    },
    validationId: {},
    required: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    valueText: {
      type: String,
      default: null
    },
    minDate: {
      type: String,
      default: null
    },
    maxDate: {
      type: String,
      default: null
    },
    formLabelClass: {
      type: String,
    },
    editMode: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    lang() {
      const supportedLangs = ['ru', 'en', 'uk']
      return supportedLangs.includes(this.$i18n.locale) ? this.$i18n.locale : 'ru'
    }
  },
  methods: {
    handleInput(e) {
      const val = moment(e).format('YYYY-MM-DD')
      this.contentString = val
      if(this.closeOnClick) this.show = false
    },
    dayClicked(e, event) {
      event?.target?.blur()
      this.$emit('dayClicked', e.id)
      this.handleInput(e.id)
    },
    focus() {
      if(this.readonly) return
      this.show = true
      this.timer = setTimeout(() => {
        this.timer = null
      }, 300)
    },
    clickedOutside() {
      if(this.timer) return
      this.show = false
    },
    manualInput() {
      if(!this.contentStringMasked || this.contentStringMasked.length === 0) {
        this.contentString = null
        return
      }
      if(this.contentStringMasked.length === 10) {
        this.contentString = moment(this.contentStringMasked, 'DD.MM.YYYY').format('YYYY-MM-DD')
        this.show = false
      }
      if(this.contentStringMasked === 'Invalid date') this.contentStringMasked = null
    },
    refresh() {
      this.contentString = this.value
      this.contentStringMasked = this.value ? moment(this.value).format('DD.MM.YYYY') : null
    },
    clear() {
      this.contentString = ''
    }
  },
  watch: {
    async contentString(val) {
      this.$emit('input', val === 'Invalid date' ? null : val)
      this.content = new Date(moment(val))
      this.contentStringMasked = moment(val).format('DD.MM.YYYY')
      if(this.minDate && val < this.minDate) {
        this.contentString = this.minDate
        await this.$nextTick()
        this.refresh()
      }
    },
    value(val) {
      if(val != this.contentString) this.refresh()
    },
  }
}
</script>

<style scoped lang="scss">
.date-picker {
  position: absolute;
  top: 45px;
  left: 0;
  &.labeled {
    top: 73px;
  }
}
.right-popup {
  .date-picker {
    left: auto;
    right: 0;
  }
}
.calendar-icon {
  top: 50%;
  right: 15px;
  width: 16px;
  height: 16px;
  margin-top: -8px;
  &.non-clickable {
    pointer-events: none;
  }
}
.overflow-ellipsis {
  padding-right: 50px;
}
@media screen and (max-width: 768px) {
  .calendar-icon {
    right: 13px;
    width: 12px;
    height: 12px;
    margin-top: -6px;
  }
  .date-picker {
    top: 31px;
    &.labeled {
      top: 51px;
    }
  }
}
</style>
